import { useCallback, useContext, useMemo } from 'react';

import { intl } from '@ocx-app/lib/translation/TranslationProvider';

import { BannerConfigurationProvider, type ExternalOfferFragment } from '@ocx/graphql';
import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import { LocalOffersLocationContext } from '@ocx/feat-local-offers';
import { useLogOfferActivity } from '@ocx/data-offers';
import { DateTimeFormat, formatDateTime } from '@ocx/utils-date';

import { ExternalBannerCard } from './external-banner-card.component';
import { useGetExternalBannerActions } from './use-get-external-banner-actions.hook';
import { useExternalBannerDetailsPopup } from '../external-banner-details-popup/external-banner-details-popup.hook';
import { externalBannerCardMessages } from './external-banner-card.messages';

import type { ExternalBannerChipData } from './external-banner-chip.component';

export type ExternalBannerCardControllerProps = {
  banner: ExternalOfferFragment;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
};

export const ExternalBannerCardController = ({ banner, hasAltriaOffersNearby }: ExternalBannerCardControllerProps) => {
  const { open: openExternalBannerDetailsPopup } = useExternalBannerDetailsPopup();
  const { logClick, logImpression } = useLogOfferActivity();
  const bannerActions = useGetExternalBannerActions(banner);

  const chipsData = useMemo<ExternalBannerChipData[]>(() => {
    if (bannerActions.ACTIVATE?.expiresAt) {
      return [
        {
          icon: 'timer',
          label: intl.formatMessage(externalBannerCardMessages['external-banner-activate-before-chip'], {
            date: formatDateTime(new Date(bannerActions.ACTIVATE?.expiresAt), DateTimeFormat.MonthDayShort),
          }),
        },
      ];
    }
    if (banner.expiresAt) {
      return [
        {
          icon: 'calendar',
          label: intl.formatMessage(externalBannerCardMessages['external-banner-expires-on-chip'], {
            date: formatDateTime(new Date(banner.expiresAt), DateTimeFormat.MonthDayShort),
          }),
        },
      ];
    }
    return [];
  }, [banner.expiresAt, bannerActions]);

  const contextData = useContext(LocalOffersLocationContext);
  const locationState = contextData?.locationState ?? null;
  const isAltriaBanner = banner.provider === BannerConfigurationProvider.Altria;
  const shouldShowUnavailableWarning =
    isAltriaBanner && hasAltriaOffersNearby === 'not-available' && locationState !== 'all';

  const onBannerCardClick = useCallback(
    (trackingId: string) => {
      logClick(trackingId, banner.externalCampaignId, banner.bannerConfigurationUuid);
      openExternalBannerDetailsPopup({
        banner,
        hasAltriaOffersNearby,
        actions: bannerActions,
        chips: chipsData,
      });
    },
    [logClick, banner, openExternalBannerDetailsPopup, hasAltriaOffersNearby, bannerActions, chipsData],
  );

  const onImpression = useCallback(
    (offerId: string) => {
      logImpression(offerId, banner.externalCampaignId, banner.bannerConfigurationUuid);
    },
    [banner.bannerConfigurationUuid, banner.externalCampaignId, logImpression],
  );

  return (
    <ExternalBannerCard
      trackingId={banner.trackingId}
      content={banner.content}
      shouldShowUnavailableWarning={shouldShowUnavailableWarning}
      onImpression={onImpression}
      onClick={onBannerCardClick}
      actions={bannerActions}
      chips={chipsData}
    />
  );
};
